// app/providers.tsx
"use client";

import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect, useState } from "react";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [posthog, setPosthog] = useState<any>(null);

  useEffect(() => {
    // Only load PostHog if we're in the browser and have the API key
    if (typeof window === "undefined" || !process.env.NEXT_PUBLIC_POSTHOG_KEY) {
      return;
    }

    // Dynamically import PostHog to reduce initial bundle size
    import("posthog-js").then((posthogModule) => {
      const posthogInstance = posthogModule.default;
      
      // Initialize PostHog with performance-optimized settings
      posthogInstance.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
        api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://app.posthog.com",
        capture_pageview: true,
        persistence: "localStorage",
        autocapture: true,
        // Capture unhandled client-side exceptions (replaces Sentry client capture)
        capture_exceptions: true,
        // Session recording: enabled in prod to catch user-stuck and error context
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "[data-ph-mask]",
        },
        loaded: (posthog) => {
          // Stamp the running commit SHA on every captured event as the `release`
          // super-property so insights/funnels slice by release (#586). Same SHA the
          // deploy annotation + sourcemap upload use; empty on local/preview builds.
          const release = process.env.NEXT_PUBLIC_RELEASE_SHA;
          if (release) {
            posthog.register({ release });
          }
          if (process.env.NODE_ENV === "development") {
            posthog.debug();
          }
        },
      });

      setPosthog(posthogInstance);
    });
  }, []);

  // Render children immediately, PostHog will initialize asynchronously
  if (typeof window === "undefined" || !posthog) {
    return <>{children}</>;
  }

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
